import { TeamRole } from '../enums/userCompanyRole.enum'
import { ICompanyMemberPure, ICompanySettings } from '../models/Company'
import {
  IMemberReleaseNotesState,
  IReleaseNote,
  ReleaseNoteAudience,
} from '../models/ReleaseNote'
import { getMemberRole, isSupport } from './isLeadOf'
import { isModuleEnabled } from './moduleAccess.helper'

// Highest role wins: admin > lead > employee. Only a team lead is a lead: a
// custom lead doesn't get the team lead features the lead notes describe.
export const getReleaseNoteAudience = (
  member: ICompanyMemberPure | undefined
): ReleaseNoteAudience | undefined => {
  if (!member || isSupport(member)) return undefined
  if (member.isAdmin) return ReleaseNoteAudience.ADMIN
  if (getMemberRole(member) === TeamRole.LEAD) return ReleaseNoteAudience.LEAD
  return ReleaseNoteAudience.EMPLOYEE
}

// A role can also view the notes of every role below it
export const getViewableAudiences = (
  audience: ReleaseNoteAudience | undefined
): ReleaseNoteAudience[] => {
  const audiences = Object.values(ReleaseNoteAudience)
  return audience ? audiences.slice(audiences.indexOf(audience)) : []
}

// Notes of the last 12 months this audience can see in this company, drafts
// (support only) first, then newest first. Future-dated notes stay hidden as a
// safety net; notes normally ship with their feature's release.
export const getNotesForCompany = (
  notes: IReleaseNote[],
  audience: ReleaseNoteAudience | undefined,
  settings: ICompanySettings | undefined,
  now: Date = new Date(),
  includeDrafts = false
): IReleaseNote[] => {
  if (!audience) return []
  const yearAgo = new Date(now)
  yearAgo.setFullYear(now.getFullYear() - 1)
  return notes
    .filter((note) => {
      const publishedAt = new Date(note.publishedAt)
      return (
        !!note.variants[audience] &&
        (note.draft
          ? includeDrafts
          : publishedAt > yearAgo && publishedAt <= now) &&
        (!note.requiredModule || isModuleEnabled(settings, note.requiredModule))
      )
    })
    .sort(
      (a, b) =>
        Number(!!b.draft) - Number(!!a.draft) ||
        b.publishedAt.localeCompare(a.publishedAt)
    )
}

export const getUnreadNotes = (
  notes: IReleaseNote[],
  state: IMemberReleaseNotesState | undefined
): IReleaseNote[] =>
  !state
    ? []
    : notes.filter(
        (note) =>
          new Date(note.publishedAt) > new Date(state.since) &&
          !state.openedIds.includes(note.id)
      )

// Only the newest note may open the popup; older unread notes just keep the dot
export const shouldAutoOpen = (
  notes: IReleaseNote[],
  state: IMemberReleaseNotesState | undefined
): boolean => !!notes[0] && getUnreadNotes([notes[0]], state).length > 0

const TERM_TOKEN = /{{term:([\w.]+)(\|lower)?}}/g

export const getReleaseNoteTermKeys = (text: string): string[] =>
  Array.from(text.matchAll(TERM_TOKEN), (match) => match[1])

export const resolveReleaseNoteTerms = (
  text: string,
  t: (key: string) => string
): string =>
  text.replace(TERM_TOKEN, (_, key: string, lower?: string) =>
    lower ? t(key).toLowerCase() : t(key)
  )
